import React, { useState, useEffect } from 'react';
import { Mail, Check, X, Heart, Bell, Minimize2 } from 'lucide-react';
import subscriptionApi from '../services/subscriptionApi';

const SubscriptionCTA = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    // Check if user has already dismissed or subscribed
    const dismissed = localStorage.getItem('elbethel_subscription_dismissed');
    const hasSubscribed = localStorage.getItem('elbethel_subscription_completed');
    
    if (hasSubscribed || dismissed) {
      setIsDismissed(true);
      return;
    }

    // Show modal after user has been on site for 15 seconds
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 15000);

    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const result = await subscriptionApi.subscribe(email, name, 'general', ['ministry_updates', 'spiritual_insights']);
      console.log('Subscription successful:', result);
      setIsSubmitted(true);
      setEmail('');
      setName('');
      
      // Mark as subscribed permanently
      localStorage.setItem('elbethel_subscription_completed', 'true');
      
      // Hide the CTA after successful submission
      setTimeout(() => {
        setIsVisible(false);
      }, 4000);
    } catch (err) {
      setError('Something went wrong. Please try again.');
      console.error('Subscription error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    setIsDismissed(true);
    // Remember dismissal for this session and longer
    localStorage.setItem('elbethel_subscription_dismissed', 'true');
  };

  const handleMinimize = () => {
    setIsMinimized(true);
    // Keep minimized for this session
    sessionStorage.setItem('elbethel_subscription_minimized', 'true');
  };

  const handleExpand = () => {
    setIsMinimized(false);
    sessionStorage.removeItem('elbethel_subscription_minimized');
  };

  // Don't show if permanently dismissed or subscribed
  if (isDismissed || !isVisible) {
    return null;
  }

  // Minimized state - small non-intrusive button
  if (isMinimized) {
    return (
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={handleExpand}
          className="w-14 h-14 bg-[#f17b21] hover:bg-[#d86919] text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110 flex items-center justify-center"
          aria-label="Subscribe to newsletter"
        >
          <Bell className="w-6 h-6" />
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 max-w-sm lg:max-w-md">
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden animate-slide-in">
        {/* Header with minimize and close buttons */}
        <div className="flex items-center justify-between p-4 bg-gradient-to-r from-[#f17b21] to-[#ff9649] text-white">
          <div className="flex items-center space-x-2">
            <Mail className="w-5 h-5" />
            <span className="font-semibold font-sans text-sm">Stay Connected</span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleMinimize}
              className="w-6 h-6 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors duration-200"
              aria-label="Minimize"
            >
              <Minimize2 className="w-3 h-3" />
            </button>
            <button
              onClick={handleClose}
              className="w-6 h-6 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors duration-200"
              aria-label="Close"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>

        {!isSubmitted ? (
          <div className="p-6">
            <div className="text-center mb-4">
              <h3 className="text-lg font-bold text-gray-800 mb-2 font-display">
                Join Our Ministry Family
              </h3>
              <p className="text-gray-600 text-sm font-sans">
                Get weekly spiritual insights and biblical encouragement delivered to your inbox.
              </p>
            </div>

            {/* Compact Form */}
            <form onSubmit={handleSubmit} className="space-y-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f17b21] focus:border-transparent text-sm font-sans"
                required
              />

              {error && (
                <div className="text-red-600 text-xs font-sans text-center">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-[#f17b21] to-[#ff9649] hover:from-[#d86919] hover:to-[#e8853a] text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed font-sans flex items-center justify-center space-x-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                    <span>Subscribing...</span>
                  </>
                ) : (
                  <>
                    <Bell className="w-4 h-4" />
                    <span>Subscribe Free</span>
                  </>
                )}
              </button>
            </form>

            {/* Benefits */}
            <div className="mt-4 text-center">
              <p className="text-xs text-gray-500 font-sans">
                🙏 Weekly spiritual insights • 📖 No spam • ✋ Unsubscribe anytime
              </p>
            </div>
          </div>
        ) : (
          <div className="p-6 text-center">
            <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-2 font-display">
              Welcome to the Family!
            </h3>
            <p className="text-gray-600 text-sm font-sans">
              Thank you for subscribing! You'll receive spiritual encouragement in your inbox.
            </p>
            <div className="mt-4 flex items-center justify-center space-x-2 text-[#f17b21]">
              <Heart className="w-4 h-4" />
              <span className="text-sm font-semibold font-sans">Blessings await!</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionCTA;